'use client';

import { useEffect, useRef, useState } from 'react';
import { ArrowLeft, Send, Loader2 } from 'lucide-react';
import { useSupportTicket, useReplySupportTicket } from '../hooks/use-support';
import {
  formatRelativeTime,
  statusLabel,
  statusToneClasses,
} from '../lib/format';

interface SupportTicketThreadProps {
  ticketId: string;
  onBack: () => void;
}

export function SupportTicketThread({
  ticketId,
  onBack,
}: SupportTicketThreadProps) {
  const { data: ticket, isLoading } = useSupportTicket(ticketId);
  const reply = useReplySupportTicket(ticketId);
  const [body, setBody] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [ticket?.messages?.length]);

  if (isLoading || !ticket) {
    return (
      <div className="flex flex-1 items-center justify-center text-neutral-400">
        <Loader2 className="h-5 w-5 animate-spin" />
      </div>
    );
  }

  async function handleSend() {
    const trimmed = body.trim();
    if (!trimmed) return;
    try {
      await reply.mutateAsync(trimmed);
      setBody('');
    } catch {
      /* surfaced via mutation state */
    }
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center gap-2 border-b border-neutral-200 px-4 py-3 dark:border-neutral-800">
        <button
          type="button"
          onClick={onBack}
          aria-label="Înapoi la opțiuni"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-neutral-100"
        >
          <ArrowLeft className="h-4 w-4" strokeWidth={1.75} />
        </button>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[0.875rem] font-medium text-neutral-900 dark:text-neutral-100">
            {ticket.subject}
          </div>
          <div className="mt-0.5 flex items-center gap-2 text-[0.6875rem] text-neutral-500 dark:text-neutral-500">
            <span
              className={`rounded-full px-1.5 py-0.5 text-[0.625rem] font-medium ${statusToneClasses(
                ticket.status,
              )}`}
            >
              {statusLabel(ticket.status)}
            </span>
            <span>· {formatRelativeTime(ticket.updatedAt)}</span>
          </div>
        </div>
      </div>

      <div
        ref={scrollRef}
        className="flex-1 space-y-3 overflow-y-auto px-4 py-4"
      >
        {ticket.messages?.map((msg) => {
          const isUser = msg.authorType === 'user';
          const isAgent = msg.authorType === 'agent';
          const isSystem = msg.authorType === 'system' || msg.authorType === 'ai';
          return (
            <div
              key={msg.id}
              className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] rounded-2xl px-3.5 py-2.5 text-[0.8125rem] leading-relaxed ${
                  isUser
                    ? 'bg-amber-500 text-white'
                    : isAgent
                    ? 'border border-neutral-200 bg-white text-neutral-900 dark:border-neutral-800 dark:bg-neutral-900 dark:text-neutral-100'
                    : 'bg-neutral-100 text-neutral-700 italic dark:bg-neutral-900 dark:text-neutral-400'
                }`}
              >
                {!isUser && !isSystem && (
                  <div className="mb-1 text-[0.625rem] font-medium uppercase tracking-wider text-neutral-500 dark:text-neutral-500">
                    Echipa SoftFactura
                  </div>
                )}
                <div className="whitespace-pre-wrap break-words">{msg.body}</div>
                <div
                  className={`mt-1 text-[0.625rem] ${
                    isUser ? 'text-amber-100/80' : 'text-neutral-400 dark:text-neutral-600'
                  }`}
                >
                  {formatRelativeTime(msg.createdAt)}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {ticket.status !== 'closed' && (
        <div className="border-t border-neutral-200 p-3 dark:border-neutral-800">
          <div className="flex items-end gap-2 rounded-xl border border-neutral-200 bg-white px-3 py-2 focus-within:border-amber-400 focus-within:ring-2 focus-within:ring-amber-100 dark:border-neutral-800 dark:bg-neutral-900 dark:focus-within:border-amber-500 dark:focus-within:ring-amber-950">
            <textarea
              rows={2}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                  e.preventDefault();
                  handleSend();
                }
              }}
              placeholder="Scrie un răspuns…"
              className="flex-1 resize-none bg-transparent text-[0.8125rem] leading-relaxed text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus-visible:outline-none focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:shadow-none dark:text-neutral-100 dark:placeholder:text-neutral-600"
            />
            <button
              type="button"
              onClick={handleSend}
              disabled={!body.trim() || reply.isPending}
              aria-label="Trimite (Cmd/Ctrl+Enter)"
              className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-amber-500 text-white transition-all hover:bg-amber-600 disabled:cursor-not-allowed disabled:bg-neutral-200 disabled:text-neutral-400 dark:disabled:bg-neutral-800 dark:disabled:text-neutral-600"
            >
              {reply.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Send className="h-4 w-4" strokeWidth={1.75} />
              )}
            </button>
          </div>
          <div className="mt-1 px-1 text-[0.625rem] text-neutral-400 dark:text-neutral-600">
            ⌘/Ctrl + Enter pentru trimitere rapidă
          </div>
        </div>
      )}
    </div>
  );
}
